import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useRecordVisit } from '@/features/app-shell/hooks/useRecordVisit';
import { AlertTriangle, ArrowLeft, ExternalLink, FileText, Loader2, RefreshCw, Plus, StickyNote } from 'lucide-react';
import { PanelSkeleton, TableSkeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PermissionGuard } from '@/components/PermissionGuard';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { usePurchaseOrderStore } from '../store/purchaseOrderStore';
import { purchaseOrderService } from '../services/purchaseOrderService';
import { useReceivingStore } from '@/features/receiving/store/receivingStore';
import { receivingService } from '@/features/receiving/services/receivingService';
import { ReceivingStatusBadge } from '@/features/receiving/components/ReceivingStatusBadge';
import { PoStatusBadge } from '../components/PoStatusBadge';
import { cn } from '@/lib/utils';
import type { PurchaseOrderLineDto } from '../types/purchaseOrder.types';
import { DeliveryTypeEnum, type ReceivingDocumentDto } from '@/features/receiving/types/receiving.types';

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function LineProgressBar({ line }: { line: PurchaseOrderLineDto }) {
  if (line.isServiceItem) {
    return <span className="text-muted-foreground text-sm">N/A</span>;
  }

  const poQty = line.poQuantity;
  const received = line.quantityReceived;
  const forPutaway = line.quantityForPutAway;
  const remaining = Math.max(0, poQty - received - forPutaway);

  const pct = (value: number) => (poQty > 0 ? Math.min(100, (value / poQty) * 100) : 0);
  const receivedPct = pct(received);
  const putawayPct = pct(forPutaway);

  const caption =
    forPutaway > 0
      ? `${received} posted · ${forPutaway} putaway / ${poQty}`
      : received >= poQty && poQty > 0
        ? `${poQty} / ${poQty} posted`
        : `${received} / ${poQty} posted`;

  const breakdown = [
    { label: 'Received (posted)', value: received, dot: 'bg-success' },
    { label: 'For Putaway', value: forPutaway, dot: 'bg-warning' },
    { label: 'Remaining', value: remaining, dot: 'bg-muted-foreground/30' },
  ];

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="group w-full cursor-help space-y-1">
            <div className="flex h-2 w-full overflow-hidden rounded-full bg-border ring-offset-background transition-all group-hover:ring-2 group-hover:ring-ring/40 group-hover:ring-offset-1">
              {receivedPct > 0 && (
                <div className="h-full bg-success transition-all" style={{ width: `${receivedPct}%` }} />
              )}
              {putawayPct > 0 && (
                <div className="h-full bg-warning transition-all" style={{ width: `${putawayPct}%` }} />
              )}
            </div>
            <p className="text-xs text-muted-foreground">{caption}</p>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" align="end" className="w-48">
          <p className="mb-1.5 font-mono text-xs font-medium">{line.itemCode}</p>
          <ul className="space-y-1">
            {breakdown.map((row) => (
              <li key={row.label} className="flex items-center justify-between gap-3 text-xs">
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <span className={cn('h-2 w-2 rounded-full', row.dot)} />
                  {row.label}
                </span>
                <span className="font-medium tabular-nums">{row.value.toLocaleString()}</span>
              </li>
            ))}
            <li className="mt-1 flex items-center justify-between gap-3 border-t pt-1 text-xs">
              <span className="text-muted-foreground">PO Qty</span>
              <span className="font-medium tabular-nums">{poQty.toLocaleString()}</span>
            </li>
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function PurchaseOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canGoBack = (window.history.state?.idx ?? 0) > 0;
  const goBack = () => canGoBack ? navigate(-1) : navigate('/mro/purchase-orders');

  const {
    selectedPurchaseOrder,
    isLoadingDetail,
    detailError,
    fetchPurchaseOrderById,
    clearSelectedPurchaseOrder,
  } = usePurchaseOrderStore();

  const { selectPurchaseOrder } = useReceivingStore();

  const [isResyncing, setIsResyncing] = useState(false);
  const [linkedDocuments, setLinkedDocuments] = useState<ReceivingDocumentDto[]>([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);

  usePageTitle(selectedPurchaseOrder ? `PO ${selectedPurchaseOrder.poNumber}` : 'Purchase Order');

  // M4 (86d44z223) — Recent, on RESOLVE. Above this component's early returns.
  useRecordVisit('purchase-order', selectedPurchaseOrder?.id, selectedPurchaseOrder?.poNumber);

  useEffect(() => {
    if (id) {
      fetchPurchaseOrderById(Number(id));
    }
    return () => {
      clearSelectedPurchaseOrder();
    };
  }, [id, fetchPurchaseOrderById, clearSelectedPurchaseOrder]);

  useEffect(() => {
    if (!selectedPurchaseOrder?.id) return;
    setIsLoadingDocs(true);
    receivingService
      .getByPurchaseOrderId(selectedPurchaseOrder.id)
      .then(setLinkedDocuments)
      .catch(() => setLinkedDocuments([]))
      .finally(() => setIsLoadingDocs(false));
  }, [selectedPurchaseOrder?.id]);

  if (isLoadingDetail) {
    return (
      <div className="space-y-6">
        <PanelSkeleton rows={6} />
        <PanelSkeleton rows={3} />
        <div className="rounded-lg border p-4">
          <TableSkeleton rows={6} cols={8} />
        </div>
      </div>
    );
  }

  if (detailError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <p className="text-destructive">{detailError}</p>
        <Button variant="outline" onClick={() => id && fetchPurchaseOrderById(Number(id))}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
        <Button variant="ghost" onClick={() => goBack()}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Purchase Orders
        </Button>
      </div>
    );
  }

  if (!selectedPurchaseOrder) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <p className="text-muted-foreground">Purchase order not found</p>
        <Button variant="outline" onClick={() => goBack()}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Purchase Orders
        </Button>
      </div>
    );
  }

  const po = selectedPurchaseOrder;

  const handleResync = async () => {
    setIsResyncing(true);
    try {
      const result = await purchaseOrderService.resync(po.id);
      if (result.errors.length > 0) {
        toast.warning(result.errors[0]);
      } else {
        toast.success('PO re-synced from NetSuite successfully');
      }
      fetchPurchaseOrderById(po.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to resync PO');
    } finally {
      setIsResyncing(false);
    }
  };

  // Receiving progress
  const totalOrdered = po.lines.reduce((sum, l) => sum + l.poQuantity, 0);
  const totalReceived = po.lines.reduce((sum, l) => sum + l.quantityReceived, 0);
  const fullyReceivedCount = po.lines.filter((l) => l.remainingQuantity <= 0).length;
  const pendingCount = po.lines.filter((l) => l.remainingQuantity > 0).length;
  const overallPct = totalOrdered > 0
    ? (totalReceived >= totalOrdered ? 100 : Math.floor((totalReceived / totalOrdered) * 100))
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => goBack()}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold font-mono">{po.poNumber}</h1>
              <p className="text-muted-foreground text-sm mt-1">
                Synced {formatDateTime(po.syncedAt)}
              </p>
            </div>
            <PoStatusBadge po={po} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleResync} disabled={isResyncing}>
            {isResyncing
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <RefreshCw className="mr-2 h-4 w-4" />}
            Resync from NetSuite
          </Button>
          <PermissionGuard permission={PERMISSIONS.RECEIVING_CREATE}>
            <Button size="sm" onClick={() => {
              selectPurchaseOrder(po);
              navigate(`/mro/receiving/new/${po.netSuitePOId}`);
            }}>
              <Plus className="mr-2 h-4 w-4" />
              Create Receiving Document
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {/* ForAdjustment Alert */}
      {po.forAdjustment && (
        <div className="flex items-start gap-3 rounded-lg border border-warning/30 bg-warning/10 p-4">
          <AlertTriangle className="h-5 w-5 text-warning mt-0.5 shrink-0" />
          <div className="space-y-1">
            <p className="font-medium text-warning">
              Flagged for Adjustment
            </p>
            <p className="text-sm text-warning">
              This PO has been flagged for adjustment after a resync from NetSuite detected changes.
              Use "Resync from NetSuite" to re-check the current status.
            </p>
          </div>
        </div>
      )}

      {/* PO Summary + Notes & Terms (tabbed) */}
      <Card>
        <Tabs defaultValue="summary">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardDescription>NetSuite PO ID: {po.netSuitePOId}</CardDescription>
              {(po.stickyNotes?.length > 0 || po.termsAndConditions) && (
                <TabsList>
                  <TabsTrigger value="summary">Summary</TabsTrigger>
                  <TabsTrigger value="notes" className="flex items-center gap-1.5">
                    <StickyNote className="h-3.5 w-3.5" />
                    Notes & Terms
                  </TabsTrigger>
                </TabsList>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <TabsContent value="summary" className="mt-0">
              <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm md:grid-cols-3">
                <div>
                  <p className="text-muted-foreground">Vendor</p>
                  <p className="font-medium">{po.vendorName || '-'}</p>
                </div>
                {po.vendorId && (
                  <div>
                    <p className="text-muted-foreground">Vendor ID</p>
                    <p className="font-medium">{po.vendorId}</p>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground">PO Date</p>
                  <p className="font-medium">{formatDate(po.poDate)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <p className="font-medium">{po.poStatus || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Delivery Type</p>
                  <p className="font-medium">
                    {{ [DeliveryTypeEnum.Local]: 'Local',
                       [DeliveryTypeEnum.ImportRawMaterials]: 'Import – Raw Mats',
                       [DeliveryTypeEnum.ImportSpareParts]: 'Import – Spare Parts',
                       [DeliveryTypeEnum.Projects]: 'Projects',
                    }[po.deliveryType] ?? 'Local'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Last Synced</p>
                  <p className="font-medium">{formatDateTime(po.syncedAt)}</p>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="notes" className="mt-0 space-y-4">
              {po.stickyNotes?.length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-muted-foreground">Sticky Notes</p>
                  {po.stickyNotes.map((note, idx) => (
                    <div key={idx} className="rounded-lg border border-warning/30 bg-warning/10 p-3">
                      <p className="text-sm whitespace-pre-wrap">{note}</p>
                    </div>
                  ))}
                </div>
              )}
              {po.termsAndConditions && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Terms & Conditions</p>
                  <div className="rounded-lg border p-3 text-sm whitespace-pre-wrap">
                    {po.termsAndConditions}
                  </div>
                </div>
              )}
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>

      {/* Receiving Progress */}
      <Card>
        <CardHeader>
          <CardTitle>Receiving Progress</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {totalReceived.toLocaleString()} of {totalOrdered.toLocaleString()} units received
              </span>
              <span className="font-medium">{overallPct}%</span>
            </div>
            <div className="w-full h-2.5 bg-border rounded-full overflow-hidden">
              <div
                className="h-full bg-success transition-all"
                style={{ width: `${overallPct}%` }}
              />
            </div>
          </div>
          <div className="flex gap-6 text-sm">
            <div>
              <span className="text-muted-foreground">Total Lines:</span>
              <span className="ml-2 font-medium">{po.lines.length}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Fully Received:</span>
              <span className="ml-2 font-medium text-success">{fullyReceivedCount}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Pending:</span>
              <span className="ml-2 font-medium text-warning">{pendingCount}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card>
        <CardHeader>
          <CardTitle>Items</CardTitle>
          <CardDescription>{po.lines.length} line item{po.lines.length !== 1 ? 's' : ''}</CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-6">Line #</TableHead>
                <TableHead>Item Code</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>UOM</TableHead>
                <TableHead className="text-right">PO Qty</TableHead>
                <TableHead className="text-right">Received</TableHead>
                <TableHead className="text-right">For Putaway</TableHead>
                <TableHead className="pr-6 w-64">Progress</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {po.lines.map((line) => {
                const isFullyReceived = line.remainingQuantity <= 0;
                const isPartial = !isFullyReceived && line.quantityReceived > 0;
                return (
                  <TableRow
                    key={line.id}
                    className={cn(
                      isFullyReceived && 'bg-success/10',
                      isPartial && 'bg-warning/10',
                    )}
                  >
                    <TableCell className="pl-6 text-muted-foreground">{line.lineNumber}</TableCell>
                    <TableCell className="font-mono text-sm">{line.itemCode}</TableCell>
                    <TableCell className="max-w-[240px]">
                      <p className="font-medium truncate">{line.itemDescription}</p>
                    </TableCell>
                    <TableCell>{line.unitOfMeasure}</TableCell>
                    <TableCell className="text-right">{line.poQuantity.toLocaleString()}</TableCell>
                    <TableCell className="text-right">{line.quantityReceived.toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      {line.quantityForPutAway > 0 && (
                        <span className="text-warning font-medium">{line.quantityForPutAway.toLocaleString()}</span>
                      )}
                    </TableCell>
                    <TableCell className="pr-6">
                      <LineProgressBar line={line} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Receiving Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Receiving Documents
          </CardTitle>
          {!isLoadingDocs && linkedDocuments.length > 0 && (
            <CardDescription>
              {linkedDocuments.length} document{linkedDocuments.length !== 1 ? 's' : ''}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="px-0">
          {isLoadingDocs ? (
            <div className="px-6">
              <TableSkeleton rows={3} cols={6} />
            </div>
          ) : linkedDocuments.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No receiving documents yet
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-6">Doc No</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Delivery No</TableHead>
                  <TableHead>Received By</TableHead>
                  <TableHead>Received Date</TableHead>
                  <TableHead className="pr-6">NetSuite IR</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {linkedDocuments.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell className="pl-6">
                      <Link
                        to={`/mro/receiving/${doc.id}`}
                        className="inline-flex items-center gap-1 font-mono font-medium text-sm underline underline-offset-2 decoration-muted-foreground/50 hover:decoration-foreground transition-colors"
                      >
                        {doc.docNo}
                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      </Link>
                    </TableCell>
                    <TableCell>
                      <ReceivingStatusBadge status={doc.receivingStatus} />
                    </TableCell>
                    <TableCell>{doc.deliveryNumber}</TableCell>
                    <TableCell>{doc.receivedByName}</TableCell>
                    <TableCell>{formatDate(doc.receivedAt)}</TableCell>
                    <TableCell className="pr-6 font-mono text-sm">
                      {doc.netSuiteItemReceiptTranId || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
